/**
 * Military command capacity — prototype implementation of the data
 * `command_provided` field (present on military buildings, previously
 * unused: training was ungated).
 *
 * Design (user-approved 2026-09-21):
 * - Command counts FORMATIONS, not heads: 1 active military formation
 *   costs 1 command, regardless of headcount. Workers/civilians cost 0.
 *   This is the Total-War half of the identity: population/housing
 *   (population_provided) gates heads (the AoE half); command gates
 *   formations. Do not conflate the two.
 * - Each faction has its own pool, drawn from its own active buildings.
 *
 * FORWARD-COMPAT (Roman organization): when the org system lands
 * (contubernium → century → cohort → legion), 1 formation = 1 command
 * still holds at every tier — a 5,000-man legion costs 1 command. That
 * sublinear scaling is the point: large organizations are logistically
 * efficient, and command capacity must never price armies by headcount.
 *
 * Mirrors the future UE command system; render-free like the rest of sim.
 */
import type { GameDB } from "../data/gamedb";
import type { BuildingDef, UnitDef } from "../data/types";
import type { PlacedBuilding } from "./buildings";
import type { Combatant } from "./combat";
import type { TechEffects } from "./research";
import type { TrainingQueues } from "./training";

/**
 * PROTOTYPE TUNING: multiplies the data `command_provided` so a handful
 * of military buildings covers a real army — no hundreds of buildings.
 * Effective slots = data command_provided × COMMAND_SCALE × research
 * multiplier (e.g. one legionary_barracks: 4 × 3 = 12 formation slots).
 * The data mirror stays byte-identical; a beta may retune this number.
 */
export const COMMAND_SCALE = 3;

/** Unit roles that consume command when fielded as a formation. */
export const MILITARY_ROLES = new Set([
  "infantry",
  "skirmisher",
  "cavalry",
  "archer",
  "siege",
]);

/**
 * Whether a formation of this unit consumes command. Rule: every
 * non-civilian role costs 1 command — infantry, cavalry, siege crews,
 * and also commanders (chieftains/tribunes), officers (captains),
 * engineers, and standard-bearer detachments, all of which are
 * trainable military formations in the data. Only `worker` (the
 * civilian role) costs 0. This is deliberately broader than
 * MILITARY_ROLES (the combat set the AI drafts into warbands): the cap
 * gates *formations*, not battlefield roles.
 */
export function consumesCommand(unit: UnitDef): boolean {
  return String(unit.extra["role"] ?? "") !== "worker";
}

export function isMilitaryUnit(unit: UnitDef): boolean {
  return MILITARY_ROLES.has(String(unit.extra["role"] ?? ""));
}

/** Data command value of one building (0 when the field is absent). */
export function commandProvided(def: BuildingDef): number {
  const v = def.extra["command_provided"];
  return typeof v === "number" && v > 0 ? v : 0;
}

/**
 * Total formation slots from a faction's active buildings, scaled by the
 * prototype constant and the research multiplier (Dilectus line).
 */
export function commandCapacity(
  buildings: PlacedBuilding[],
  fx: TechEffects,
): number {
  let base = 0;
  for (const b of buildings) {
    if (b.state !== "active") continue;
    base += commandProvided(b.def);
  }
  return Math.floor(base * COMMAND_SCALE * fx.commandMultiplier());
}

/** Active command-consuming formations (1 formation = 1 command, any headcount). */
export function militaryFormationCount(
  fighters: Combatant[],
  team: 0 | 1,
): number {
  let n = 0;
  for (const f of fighters) {
    if (f.team === team && f.alive > 0 && consumesCommand(f.unit)) {
      n++;
    }
  }
  return n;
}

/** Queued command-consuming orders — reserved command, not yet on the field. */
export function queuedMilitaryCount(queues: TrainingQueues, db: GameDB): number {
  let n = 0;
  for (const o of queues.allOrders()) {
    const u = db.units.get(o.unitId);
    if (u && consumesCommand(u)) n++;
  }
  return n;
}

export interface CommandStatus {
  used: number;
  total: number;
}

/**
 * A faction's command pool: active formations + queued military orders
 * against capacity. Callers pass only their own team's buildings/fighters
 * and queues, so pools stay per-faction.
 */
export function commandStatus(
  fighters: Combatant[],
  team: 0 | 1,
  queues: TrainingQueues,
  db: GameDB,
  buildings: PlacedBuilding[],
  fx: TechEffects,
): CommandStatus {
  return {
    used:
      militaryFormationCount(fighters, team) +
      queuedMilitaryCount(queues, db),
    total: commandCapacity(buildings, fx),
  };
}

/**
 * May this unit start training under the given command status?
 * Workers/civilians never consume command — only formations do.
 */
export function canTrain(
  unit: UnitDef,
  status: CommandStatus,
): { ok: boolean; reason: string } {
  if (!consumesCommand(unit)) return { ok: true, reason: "" };
  if (status.used >= status.total) {
    return {
      ok: false,
      reason: `Command capacity reached (${status.used}/${status.total})`,
    };
  }
  return { ok: true, reason: "" };
}
